import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QuoteForm = ({ variant = "default" }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    property_size: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success("Quote request submitted successfully! We'll contact you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        service_type: "",
        property_size: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompact = variant === "compact";

  return (
    <form 
      onSubmit={handleSubmit} 
      className={`space-y-4 ${isCompact ? "" : "space-y-5"}`}
      data-testid="quote-form"
    >
      <div className={isCompact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor="name" className="form-label">Full Name *</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Smith"
            required
            className="form-input"
            data-testid="quote-name-input"
          />
        </div>
        <div>
          <Label htmlFor="email" className="form-label">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            required
            className="form-input"
            data-testid="quote-email-input"
          />
        </div>
      </div>

      <div className={isCompact ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor="phone" className="form-label">Phone Number *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            required
            className="form-input"
            data-testid="quote-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="service_type" className="form-label">Service Needed *</Label>
          <Select
            value={formData.service_type}
            onValueChange={(value) => handleSelectChange("service_type", value)}
            required
          >
            <SelectTrigger className="form-input" data-testid="quote-service-select">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="z-[200]">
              <SelectItem value="lawn-mowing" data-testid="service-lawn-mowing">Lawn Mowing</SelectItem>
              <SelectItem value="lawn-maintenance" data-testid="service-lawn-maintenance">Lawn Maintenance</SelectItem>
              <SelectItem value="weed-control" data-testid="service-weed-control">Weed Control</SelectItem>
              <SelectItem value="fertilization" data-testid="service-fertilization">Fertilization</SelectItem>
              <SelectItem value="yard-cleanup" data-testid="service-yard-cleanup">Yard Cleanup</SelectItem>
              <SelectItem value="landscaping" data-testid="service-landscaping">Landscaping</SelectItem>
              <SelectItem value="other" data-testid="service-other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isCompact && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="address" className="form-label">Property Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, Houston, TX"
                className="form-input"
                data-testid="quote-address-input"
              />
            </div>
            <div>
              <Label htmlFor="property_size" className="form-label">Property Size</Label>
              <Select
                value={formData.property_size}
                onValueChange={(value) => handleSelectChange("property_size", value)}
              >
                <SelectTrigger className="form-input" data-testid="quote-size-select">
                  <SelectValue placeholder="Select property size" />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <SelectItem value="small" data-testid="size-small">Small (Under 1/4 acre)</SelectItem>
                  <SelectItem value="medium" data-testid="size-medium">Medium (1/4 - 1/2 acre)</SelectItem>
                  <SelectItem value="large" data-testid="size-large">Large (1/2 - 1 acre)</SelectItem>
                  <SelectItem value="xlarge" data-testid="size-xlarge">Extra Large (Over 1 acre)</SelectItem>
                  <SelectItem value="commercial" data-testid="size-commercial">Commercial Property</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="message" className="form-label">Additional Details</Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your lawn care needs..."
              rows={4}
              className="form-input resize-none"
              data-testid="quote-message-input"
            />
          </div>
        </>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
        data-testid="quote-submit-btn"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting...
          </>
        ) : (
          "Get My Free Quote"
        )}
      </Button>

      <p className="text-sm text-center text-gray-500">
        We'll respond within 24 hours. No spam, ever.
      </p>
    </form>
  );
};
